import plur from 'plur';
import type { ExperimentConfig } from './entity';

// Mirrors services/domains/experiments/config.go: hours win when set, and days then carry the
// hours rounded up, so reading only duration_days overstates an hours-based span.
export type DurationUnit = 'days' | 'hours';

export interface Duration {
  amount: number;
  unit: DurationUnit;
}

export type DurationConfig = Pick<ExperimentConfig, 'duration_days' | 'duration_hours'>;

const HOURS_PER_DAY = 24;
const SINGULAR_UNIT = { days: 'day', hours: 'hour' } satisfies Record<DurationUnit, string>;

export function durationOf(config: DurationConfig): Duration {
  const hours = config.duration_hours ?? 0;
  if (hours > 0) return { amount: hours, unit: 'hours' };
  return { amount: config.duration_days, unit: 'days' };
}

export function durationInHours(config: DurationConfig): number {
  const duration = durationOf(config);
  return duration.unit === 'hours' ? duration.amount : duration.amount * HOURS_PER_DAY;
}

/** The span in days, fractional for an hours-based one. */
export function durationInDays(config: DurationConfig): number {
  return durationInHours(config) / HOURS_PER_DAY;
}

/** "1 hour", "12 hours", "1 day", "14 days". */
export function durationLabel(duration: Duration): string {
  return `${duration.amount} ${plur(SINGULAR_UNIT[duration.unit], duration.amount)}`;
}
